/**
 * CartContext — LAUNCH:C9.P3.R (MDM-LAUNCH-C9P3R-001 R1)
 *
 * Shared cart state lifted from App.jsx so ROUTED pages (Supply Lab) reach the
 * same cart as the store SPA. Behavior-preserving extraction of the App.jsx
 * cart block (localStorage persistence, stock-capped adds, notifications).
 *
 * addToCart gains an optional quantity param (default 1) for the Supply Lab
 * quantity selector; existing single-add callers are unchanged. Quantity caps
 * use the cart item's own captured stock (server-side StockReservation remains
 * the authoritative stock gate at checkout).
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNotification } from './NotificationProvider';
import {
  LINE_KIND_PRODUCT,
  MAX_CART_ITEMS,
  MAX_QUANTITY_PER_ITEM,
  lineKey,
  lineKind,
  resolveProductId,
} from '../config/cartContract';

const CartContext = createContext(null);

/**
 * ⛔ VALID JSON OF THE WRONG SHAPE IS NOT A PARSE ERROR.
 *
 * The parse was already guarded by try/catch — but `{"x":1}`, `"str"` and `123` all
 * parse CLEANLY, sail through that catch, and then throw
 * "cart.reduce is not a function" during render. main.jsx wraps the app in
 * Sentry.withProfiler, NOT withErrorBoundary, so nothing catches it: the user gets a
 * permanent white screen escapable only by clearing site data.
 *
 * ⭐ AND THE SAME CLASS EXISTS ONE LEVEL DOWN. `[{"x":1}]` is an ARRAY, so an
 * Array.isArray check alone passes it — and `item.price * item.quantity` is then NaN,
 * rendering "$NaN" on the checkout button. Not a crash, but a storefront that cannot
 * be checked out of. Both levels are filtered here.
 *
 * Dropping a malformed item is recoverable (re-add it). A white screen and a $NaN
 * total are not.
 */
// ⛔ THE CHECKOUT CONTRACT, MIRRORED. schemas/checkout.py:21-22 is the real authority:
//     product_id: int = Field(..., gt=0)
//     quantity:   int = Field(..., gt=0, le=100)
// An earlier version of this guard validated SHAPE (is it an object, are the numbers
// numbers) but not the CONTRACT the checkout endpoint enforces. Per-clause mutation
// cannot find that: it proves every clause that EXISTS is load-bearing, and is
// structurally blind to a clause that SHOULD exist. The gap reached exactly the outcome
// this guard was built to prevent -- a cart that looks perfect and cannot be paid for:
//     {price: 10, quantity: 1}      no id      -> product_id: undefined -> 422
//     {price: 10, quantity: 1.5}    fractional -> int parse fails       -> 422
//     {price: 10, quantity: 500}    over le=100                         -> 422
// all of which passed a finite-and-positive check and rendered in the cart.


const isWellFormedCartItem = (item) => {
  if (item === null || typeof item !== 'object' || Array.isArray(item)) return false;
  // ⛔ RESOLVED BY THE SHARED FUNCTION, NOT RE-DERIVED HERE. This line previously read
  // `Number(item.product_id ?? item.id)` while CheckoutForm sent `product_id || id` --
  // the comment stated the requirement correctly and the code below it did something
  // else. `{product_id: 0, id: 7}` was REJECTED here and PAYABLE there, so the guard
  // deleted an item the buyer had chosen and checkout would have accepted.
  const id = Number(resolveProductId(item));
  if (!Number.isInteger(id) || id <= 0) return false;
  const price = Number(item.price);
  const quantity = Number(item.quantity);
  if (!Number.isInteger(quantity) || quantity > MAX_QUANTITY_PER_ITEM) return false;
  // ⛔ FINITE IS NOT ENOUGH, and the coercion table is why:
  //     Number(null) === 0   Number('') === 0   Number([]) === 0   Number(-5) === -5
  // all finite. A finite-only check admits BOTH a phantom zero-quantity row AND --
  // the one that matters -- a NEGATIVE PRICE, which silently REDUCES the displayed
  // cart total. localStorage is the buyer's own, so this is attacker-editable input.
  // The server re-derives the charge, so this is not the last line of defence, but a
  // cart showing a discount the checkout will not honour is its own defect.
  //   price    >= 0 : a negative price cannot reduce a total (0 stays legal: freebies)
  //   quantity >  0 : an item with quantity 0 is not in the cart
  // ⚠ NO Number.isFinite(quantity) HERE, DELIBERATELY. Clause mutation showed it
  // SURVIVED deletion, and unlike the array-item clause there is no input that can
  // separate the two: Number.isInteger(x) STRICTLY IMPLIES Number.isFinite(x)
  // (Number.isInteger(Infinity) is already false), so the integer check above subsumes
  // it. A clause no input can distinguish is decoration, not defence.
  //   price keeps its isFinite check because price is NOT integer-checked -- decimals
  //   are legal, so Infinity would otherwise pass `price >= 0`.
  return Number.isFinite(price) && price >= 0 && quantity > 0;
};

export const sanitizeStoredCart = (parsed) =>
  Array.isArray(parsed) ? parsed.filter(isWellFormedCartItem) : [];

export function CartProvider({ children }) {
  const { showNotification } = useNotification();

  // FE-STATE-001: Persist cart to localStorage to survive page refresh
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('mdm_cart');
      return sanitizeStoredCart(saved ? JSON.parse(saved) : []);
    } catch {
      return [];
    }
  });

  // FE-STATE-001: Persist cart to localStorage on changes
  useEffect(() => {
    try {
      localStorage.setItem('mdm_cart', JSON.stringify(cart));
    } catch {
      // Ignore localStorage errors (quota exceeded, private browsing, etc.)
    }
  }, [cart]);

  // Per constitution_ui.json §6: "Every action returns feedback state; critical flows persist"
  const addToCart = useCallback((product, quantity = 1) => {
    setCart(prevCart => {
      // ⛔ MATCHED ON KIND + ID, NEVER ON ID ALONE. Bundles and products have INDEPENDENT id
      // sequences, so Bundle 7 and Product 7 collide on `.id`: adding one then the other would
      // silently increment the first's quantity. The buyer would get two of the thing they
      // asked for once, none of the thing they asked for second, and a total that looks right.
      const key = lineKey(product);
      const existing = prevCart.find(item => lineKey(item) === key);
      if (existing) {
        // The contract also caps a single line at 100 (schemas/checkout.py:22 le=100).
        // Same principle: refuse the add, do not clamp silently.
        if (existing.quantity + quantity > MAX_QUANTITY_PER_ITEM) {
          setTimeout(
            () => showNotification(`Maximum ${MAX_QUANTITY_PER_ITEM} per item`, "error"),
            0
          );
          return prevCart;
        }
        // ⚠ A BUNDLE HAS NO `stock` FIELD -- it has available_qty, and the SERVER enforces it
        // against real component stock at checkout. `undefined` comparisons are always false,
        // so this guard would silently never fire for a bundle; skipping it explicitly says so
        // rather than leaving a dead comparison that reads as protection.
        if (lineKind(product) === LINE_KIND_PRODUCT && existing.quantity + quantity > product.stock) {
          // Can't add more - show notification but return unchanged cart
          // Use setTimeout(0) to escape the setState batching
          setTimeout(() => showNotification("Max stock reached", "error"), 0);
          return prevCart;
        }
        setTimeout(() => showNotification(`Added another ${product.name} to cart`), 0);
        return prevCart.map(item =>
          lineKey(item) === key
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      // ⛔ REFUSE THE ACTION THAT EXCEEDS THE LIMIT -- never silently repair state the
      // buyer owns. schemas/checkout.py caps items at 50, so a 51st line would 422 at
      // create-payment-intent. Trimming the cart to fit would destroy something the
      // buyer chose, with no notice and no way to know which item went; the worst
      // outcome here is paying for 50 of the 51 things you meant to buy and not
      // noticing. Blocking at the point of action costs one item they can see.
      if (prevCart.length >= MAX_CART_ITEMS) {
        setTimeout(
          () => showNotification(`Cart is full (${MAX_CART_ITEMS} items max)`, "error"),
          0
        );
        return prevCart;
      }
      setTimeout(() => showNotification(`${product.name} added to cart`), 0);
      // `kind` is recorded on the line so the checkout payload can send bundle_id rather than
      // product_id, and so a reload from localStorage still knows what each line is.
      return [...prevCart, { ...product, kind: lineKind(product), quantity }];
    });
  }, [showNotification]);

  // ⛔ THESE MATCH ON KIND + ID TOO. An id-only match would let a quantity change or a removal
  // aimed at Bundle 7 hit Product 7 instead -- the buyer deletes one line and watches a
  // different one disappear. `kind` defaults to product, which is correct for every existing
  // caller and for every line written before bundles existed.
  const updateQuantity = useCallback((productId, newQuantity, kind = LINE_KIND_PRODUCT) => {
    const key = lineKey({ id: productId, kind });
    setCart(prevCart => {
      if (newQuantity <= 0) {
        setTimeout(() => showNotification("Item removed from cart"), 0);
        return prevCart.filter(item => lineKey(item) !== key);
      }
      const item = prevCart.find(i => lineKey(i) === key);
      // A bundle carries no `stock`; the server enforces availability against real component
      // stock at checkout. typeof keeps that an explicit skip rather than a silent no-op.
      if (item && typeof item.stock === 'number' && newQuantity > item.stock) return prevCart;

      return prevCart.map(i =>
        lineKey(i) === key ? { ...i, quantity: newQuantity } : i
      );
    });
  }, [showNotification]);

  const removeFromCart = useCallback((productId, kind = LINE_KIND_PRODUCT) => {
    const key = lineKey({ id: productId, kind });
    setCart(prevCart => prevCart.filter(item => lineKey(item) !== key));
    showNotification("Item removed from cart");
  }, [showNotification]);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cart]
  );
  const cartCount = useMemo(
    () => cart.reduce((sum, item) => sum + item.quantity, 0),
    [cart]
  );

  const value = useMemo(
    () => ({ cart, addToCart, updateQuantity, removeFromCart, clearCart, cartTotal, cartCount }),
    [cart, addToCart, updateQuantity, removeFromCart, clearCart, cartTotal, cartCount]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (ctx === null) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return ctx;
}
